// src/pages/user/BrandSettings.jsx
import { useEffect, useState, useRef } from "react";
import {
  Upload, X, Check, Edit3, Save, AlertCircle, Mail, Globe,
} from "lucide-react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { FaBuilding, FaGlobe, FaPhone } from "react-icons/fa6";
import { HiMapPin } from "react-icons/hi2";
import FeatureGate from "../../components/FeatureGate";
import { useAuth } from "../../auth/AuthContext";
import toast from "react-hot-toast";
import { injectUserStyles } from "./userStyles";

const MAIN_BASE_DOMAIN   = process.env.REACT_APP_PANEL_URL  || "panel.mydesignerplus.com";
const EDITOR_BASE_DOMAIN = process.env.REACT_APP_EDITOR_DOMAIN || "app.mydesignerplus.com";

const STYLE_ID = "brand-settings-styles";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .bs-wrap { font-family:'DM Sans',sans-serif; color:#0c1118; }
    .bs-section { background:#fff; border:1px solid #e8ecf0; border-radius:10px; overflow:hidden; margin-bottom:20px; }
    .bs-section-head { padding:16px 20px; border-bottom:1px solid #f3f4f6; display:flex; align-items:center; gap:12px; }
    .bs-section-icon { width:36px; height:36px; border-radius:8px; background:#f4f5f7; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .bs-section-title { font-size:14px; font-weight:600; color:#0c1118; }
    .bs-section-sub   { font-size:12px; color:#9ca3af; margin-top:2px; }
    .bs-section-body  { padding:20px 24px; }
    .bs-form-grid   { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:16px; }
    @media (max-width:767px) { .bs-form-grid { grid-template-columns:1fr; } }
    .bs-field { display:flex; flex-direction:column; gap:6px; }
    .bs-field-label { font-family:'DM Sans',sans-serif; font-size:10px; letter-spacing:.04em; color:#6b7280; display:flex; align-items:center; gap:5px; }
    .bs-input {
      padding:9px 12px; border:1px solid #e2e8f0; border-radius:7px; font-size:14px;
      color:#0c1118; outline:none; transition:border-color 150ms, box-shadow 150ms;
      font-family:'DM Sans',sans-serif; width:100%; box-sizing:border-box;
    }
    .bs-input:focus { border-color:#f9ab14; box-shadow:0 0 0 3px rgba(249,171,20,.1); }
    .bs-textarea { resize:vertical; min-height:90px; }
    .bs-status-ok  { display:flex; align-items:center; gap:6px; font-size:12.5px; color:#10b981; }
    .bs-status-err { display:flex; align-items:center; gap:6px; font-size:12.5px; color:#dc2626; }
    .bs-favicon-row { display:flex; flex-wrap:wrap; gap:24px; align-items:flex-start; }
    .bs-favicon-preview {
      width:96px; height:96px; border-radius:10px;
      border:2px dashed #e2e8f0; background:#f9fafb;
      display:flex; align-items:center; justify-content:center;
      overflow:hidden; flex-shrink:0;
    }
    .bs-favicon-preview img { max-width:100%; max-height:100%; object-fit:contain; padding:8px; }
    .bs-favicon-actions { display:flex; flex-direction:column; gap:10px; }
    .bs-favicon-hint { display:flex; align-items:flex-start; gap:8px; padding:10px 12px; background:rgba(59,130,246,.06); border:1px solid rgba(59,130,246,.15); border-radius:7px; font-size:12px; color:#1e40af; }
    .bs-slug-row { display:flex; flex-wrap:wrap; gap:10px; align-items:flex-start; margin-bottom:12px; }
    .bs-slug-input { width:220px; }
    .bs-preview-box { background:#f9fafb; border:1px solid #e8ecf0; border-radius:8px; padding:14px 16px; }
    .bs-preview-row { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:6px 0; border-bottom:1px solid #f3f4f6; }
    .bs-preview-row:last-child { border-bottom:none; }
    .bs-preview-label { font-size:12.5px; color:#6b7280; }
    .bs-preview-val   { font-family:'DM Sans',sans-serif; font-size:12px; color:#0c1118; }
    .bs-logo-grid { display:grid; grid-template-columns:1fr 1fr; gap:24px; }
    @media (max-width:1023px) { .bs-logo-grid { grid-template-columns:1fr; } }
    .bs-logo-preview-box {
      width:100%; aspect-ratio:16/9; background:#f4f5f7;
      border:1px solid #e8ecf0; border-radius:8px; overflow:hidden;
      display:flex; align-items:center; justify-content:center;
    }
    .bs-logo-preview-box img { width:100%; height:100%; object-fit:contain; padding:12px; }
    .bs-access-card { background:#fff; border:1px solid #e8ecf0; border-radius:10px; padding:18px 20px; }
    .bs-toggle-wrap { display:flex; align-items:center; gap:8px; }
    .bs-toggle-input { width:16px; height:16px; accent-color:#f9ab14; cursor:pointer; }
  `;
  document.head.appendChild(el);
}

/* ── Main component ── */
export default function BrandSettings() {
  useEffect(() => { injectLocal(); }, []);

  const [loading,  setLoading]  = useState(true);
  const [brand,    setBrand]    = useState(null);
  const [editingPreviewUrl, setEditingPreviewUrl] = useState(null);

  const [infoSaving, setInfoSaving] = useState(false);
  const [infoStatus, setInfoStatus] = useState(null);

  const [slug,       setSlug]       = useState("");
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null);
  const [slugError,  setSlugError]  = useState("");

  const [faviconSaving, setFaviconSaving] = useState(false);
  const [faviconStatus, setFaviconStatus] = useState(null);

  const { refresh } = useAuth();

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand/me`, { credentials: "include" });
        if (!res.ok) { setBrand(null); return; }
        const data = await res.json();
        const b = data?.brand || null;
        setBrand(b);
        if (b?.subdomainSlug) setSlug(b.subdomainSlug);
      } catch (e) {
        console.error("Failed to load brand", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => { if (brand?.subdomainSlug && !slug) setSlug(brand.subdomainSlug); }, [brand?.subdomainSlug]);

  const dashboardPreview = slug ? `${slug}.${MAIN_BASE_DOMAIN}`   : `yourname.${MAIN_BASE_DOMAIN}`;
  const editorPreview    = slug ? `${slug}.${EDITOR_BASE_DOMAIN}` : `yourname.${EDITOR_BASE_DOMAIN}`;

  async function handleInfoSubmit(e) {
    e.preventDefault(); setInfoSaving(true); setInfoStatus(null);
    try {
      const f = e.currentTarget;
      const payload = { companyName: f.companyName.value || null, phone: f.phone.value || null, email: f.email.value || null, website: f.website.value || null, address: f.address.value || null };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand/me`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) { console.error("Failed to save company info", data); setInfoStatus("error"); return; }
      setBrand(data.brand || payload); setInfoStatus("success");
      if (typeof refresh === "function") refresh();
    } catch (e) { console.error(e); setInfoStatus("error"); }
    finally { setInfoSaving(false); setTimeout(() => setInfoStatus(null), 3000); }
  }

  function validateSlug(value) {
    const s = value.trim().toLowerCase();
    if (!s) { setSlugError("Slug is required."); return null; }
    if (!/^[a-z0-9-]{3,63}$/.test(s)) { setSlugError("Use 3–63 characters: lowercase letters, numbers, and hyphens only."); return null; }
    setSlugError(""); return s;
  }

  async function handleSaveSlug() {
    const normalized = validateSlug(slug); if (!normalized) return;
    setSlugSaving(true); setSlugStatus(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand/domains/slug`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ slug: normalized }) });
      const data = await res.json();
      if (!res.ok) { console.error("Slug update failed", data); setSlugStatus("error"); if (data?.message) setSlugError(data.message); return; }
      setBrand(data.brand || null); setSlug(data.brand?.subdomainSlug || normalized); setSlugStatus("success");
      if (typeof refresh === "function") refresh();
    } catch (e) { console.error(e); setSlugStatus("error"); }
    finally { setSlugSaving(false); setTimeout(() => setSlugStatus(null), 4000); }
  }

  async function handleFaviconFile(file) {
    if (!file) return;
    setFaviconSaving(true); setFaviconStatus(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand/favicon`, { method: "POST", credentials: "include", body: fd });
      const data = await res.json();
      if (!res.ok) { console.error("Favicon upload failed", data); setFaviconStatus("error"); return; }
      setBrand((prev) => ({ ...(prev || {}), ...(data.brand || {}) })); setFaviconStatus("success");
      if (typeof refresh === "function") refresh();
    } catch (e) { console.error(e); setFaviconStatus("error"); }
    finally { setFaviconSaving(false); setTimeout(() => setFaviconStatus(null), 4000); }
  }

  async function handleRemoveFavicon() {
    setFaviconSaving(true); setFaviconStatus(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand/favicon`, { method: "DELETE", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { console.error("Favicon delete failed", data); setFaviconStatus("error"); return; }
      setBrand((prev) => ({ ...(prev || {}), faviconUrl: null, faviconFileId: null })); setFaviconStatus("success");
      if (typeof refresh === "function") refresh();
    } catch (e) { console.error(e); setFaviconStatus("error"); }
    finally { setFaviconSaving(false); setTimeout(() => setFaviconStatus(null), 4000); }
  }

  if (loading) return <div className="up-loading"><div className="up-spinner" style={{ width: 24, height: 24 }} /> Loading brand settings…</div>;

  return (
    <FeatureGate required={["feat.branding"]} requireActiveSub={true}
      title="Custom Branding is not available on your current plan"
      intro="Upgrade your company plan to unlock white-label branding features.">
      <div className="bs-wrap">
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <div className="up-title">Brand Settings</div>
          <div className="up-sub">Customize your company branding and white-label appearance</div>
        </div>

        {/* ── Logo & Favicon ── */}
        <div className="bs-section">
          <div className="bs-section-head">
            <div className="bs-section-icon" style={{ background: "rgba(249,171,20,.1)" }}>
              <FaCloudUploadAlt style={{ color: "#f9ab14", fontSize: "16px" }} />
            </div>
            <div>
              <div className="bs-section-title">Company Logo &amp; Favicon</div>
              <div className="bs-section-sub">Upload your logo and favicon to white-label your dashboard and editor</div>
            </div>
          </div>

          {/* Logo */}
          <div className="bs-section-body" style={{ borderBottom: "1px solid #f3f4f6" }}>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#0c1118", marginBottom: "3px" }}>Company Logo</div>
              <div style={{ fontSize: "12px", color: "#9ca3af" }}>Upload and crop your logo for use across the platform</div>
            </div>
            <div className="bs-logo-grid">
              <div>
                <LogoPickerCard
                  onConfirmed={(url) => { setBrand((b) => ({ ...(b || {}), logoUrl: url })); setEditingPreviewUrl(null); }}
                  onEditingPreview={(url) => setEditingPreviewUrl(url)}
                  onEditingEnd={() => setEditingPreviewUrl(null)}
                />
              </div>
              <LogoPreviewCard
                url={editingPreviewUrl || brand?.logoUrl || null}
                hasSaved={!!brand?.logoUrl}
                isEditing={!!editingPreviewUrl}
                onRemoved={() => setBrand((b) => ({ ...(b || {}), logoUrl: null, logoFileId: null }))}
              />
            </div>
          </div>

          {/* Favicon */}
          <div className="bs-section-body" style={{ background: "#fafafa" }}>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#0c1118", marginBottom: "3px" }}>Favicon</div>
              <div style={{ fontSize: "12px", color: "#9ca3af" }}>Used in browser tabs — we'll automatically size it appropriately</div>
            </div>
            <div className="bs-favicon-row">
              <div className="bs-favicon-preview">
                {brand?.faviconUrl
                  ? <img src={brand.faviconUrl} alt="Favicon" />
                  : <div style={{ textAlign: "center" }}><Upload size={20} style={{ color: "#d1d5db", margin: "0 auto" }} /><div style={{ fontSize: "10px", color: "#d1d5db", marginTop: "4px" }}>No favicon</div></div>}
              </div>
              <div className="bs-favicon-actions">
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <label className="up-btn up-btn-dark" style={{ cursor: "pointer" }}>
                    <Upload size={13} /> {faviconSaving ? "Uploading…" : "Upload Favicon"}
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => handleFaviconFile(e.target.files?.[0])} disabled={faviconSaving} />
                  </label>
                  {brand?.faviconUrl && (
                    <button className="up-btn up-btn-outline" style={{ color: "#dc2626" }} onClick={handleRemoveFavicon} disabled={faviconSaving}>
                      <X size={13} /> Remove
                    </button>
                  )}
                </div>
                {faviconStatus === "success" && <div className="bs-status-ok"><Check size={13} /> Favicon updated successfully</div>}
                {faviconStatus === "error"   && <div className="bs-status-err"><AlertCircle size={13} /> Failed to update favicon. Please try again.</div>}
                <div className="bs-favicon-hint">
                  <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div><strong>Recommended specs:</strong> PNG • 16×16, 32×32, or 48×48 px • Max 1 MB</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Company Information ── */}
        <div className="bs-section">
          <div className="bs-section-head">
            <div className="bs-section-icon" style={{ background: "rgba(16,185,129,.1)" }}>
              <FaBuilding style={{ color: "#10b981", fontSize: "15px" }} />
            </div>
            <div>
              <div className="bs-section-title">Company Information</div>
              <div className="bs-section-sub">Update your business contact details</div>
            </div>
          </div>
          <form onSubmit={handleInfoSubmit} className="bs-section-body">
            <div className="bs-form-grid">
              <div className="bs-field">
                <label className="bs-field-label"><FaBuilding size={11} /> Company Name</label>
                <input name="companyName" className="bs-input" placeholder="Enter your company name" defaultValue={brand?.companyName || ""} />
              </div>
              <div className="bs-field">
                <label className="bs-field-label"><FaPhone size={11} /> Phone Number</label>
                <input name="phone" className="bs-input" placeholder="+1 (555) 123-4567" defaultValue={brand?.phone || ""} />
              </div>
              <div className="bs-field">
                <label className="bs-field-label"><Mail size={11} /> Email Address</label>
                <input name="email" type="email" className="bs-input" placeholder="contact@company.com" defaultValue={brand?.email || ""} />
              </div>
              <div className="bs-field">
                <label className="bs-field-label"><FaGlobe size={11} /> Website</label>
                <input name="website" type="url" className="bs-input" placeholder="www.company.com" defaultValue={brand?.website || ""} />
              </div>
            </div>
            <div className="bs-field" style={{ marginBottom: "20px" }}>
              <label className="bs-field-label"><HiMapPin size={11} /> Business Address</label>
              <textarea name="address" rows={3} className={`bs-input bs-textarea`} placeholder="Street, City, State, ZIP, Country" defaultValue={brand?.address || ""} />
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px" }}>
              {infoStatus === "error"   && <div className="bs-status-err"><AlertCircle size={13} /> Failed to save changes.</div>}
              {infoStatus === "success" && <div className="bs-status-ok"><Check size={13} /> Changes saved.</div>}
              <button type="submit" disabled={infoSaving} className="up-btn up-btn-dark">
                {infoSaving ? <><div className="up-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</> : <><Save size={13} /> Save Changes</>}
              </button>
            </div>
          </form>
        </div>

        {/* ── Custom Subdomain ── */}
        <div className="bs-section">
          <div className="bs-section-head">
            <div className="bs-section-icon" style={{ background: "rgba(139,92,246,.1)" }}>
              <Globe size={15} style={{ color: "#8b5cf6" }} />
            </div>
            <div>
              <div className="bs-section-title">Custom Subdomain</div>
              <div className="bs-section-sub">Choose a slug once, and we will generate both dashboard and editor domains</div>
            </div>
          </div>
          <div className="bs-section-body">
            <div className="bs-field" style={{ marginBottom: "16px" }}>
              <label className="bs-field-label"><Globe size={11} /> Subdomain Slug</label>
              <div className="bs-slug-row">
                <input
                  value={slug}
                  onChange={(e) => { setSlug(e.target.value.toLowerCase()); setSlugStatus(null); }}
                  className={`bs-input bs-slug-input`}
                  placeholder="yourcompany"
                />
                <button type="button" onClick={handleSaveSlug} disabled={slugSaving} className="up-btn up-btn-dark">
                  {slugSaving ? <><div className="up-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</> : <><Save size={13} /> Save Slug</>}
                </button>
              </div>
              <div style={{ fontSize: "11.5px", color: "#9ca3af", marginTop: "4px" }}>3–63 lowercase characters; letters, numbers, and hyphens only.</div>
              {slugError   && <div className="bs-status-err" style={{ marginTop: "6px" }}><AlertCircle size={12} /> {slugError}</div>}
              {slugStatus === "success" && <div className="bs-status-ok"   style={{ marginTop: "6px" }}><Check size={12} /> Subdomain updated.</div>}
              {slugStatus === "error"   && <div className="bs-status-err"  style={{ marginTop: "6px" }}><AlertCircle size={12} /> Failed to save slug. Try another value.</div>}
            </div>
            <div className="bs-preview-box">
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", letterSpacing: ".04em", color: "#9ca3af", marginBottom: "8px" }}>Preview</div>
              <div className="bs-preview-row">
                <span className="bs-preview-label">Dashboard domain</span>
                <span className="bs-preview-val">{dashboardPreview}</span>
              </div>
              <div className="bs-preview-row">
                <span className="bs-preview-label">Editor domain</span>
                <span className="bs-preview-val">{editorPreview}</span>
              </div>
            </div>
          </div>
        </div>

        <EditorAccessCard />
      </div>
    </FeatureGate>
  );
}

/* ── LogoPickerCard (preserved exactly) ── */
function LogoPickerCard({ onConfirmed, onEditingPreview, onEditingEnd }) {
  const VIEW_W = 520, VIEW_H = 320, MIN_W = 32, MIN_H = 32, OUT_MAX = 1200;
  const { refresh } = useAuth();
  const [mode, setMode] = useState("idle");
  const [sourceUrl, setSourceUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const imgEl = useRef(null);
  const [disp, setDisp] = useState({ w: 0, h: 0, scale: 1, offsetX: 0, offsetY: 0 });
  const [box, setBox] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const dragRef   = useRef({ active: false, ox: 0, oy: 0, sx: 0, sy: 0 });
  const resizeRef = useRef({ active: false, corner: null, ox: 0, oy: 0, x: 0, y: 0, w: 0, h: 0 });

  function onPick(file) { if (!file) return; if (sourceUrl) URL.revokeObjectURL(sourceUrl); setSourceUrl(URL.createObjectURL(file)); setMode("editing"); setErr(""); }
  function cancel() { if (sourceUrl) URL.revokeObjectURL(sourceUrl); setSourceUrl(null); setMode("idle"); setErr(""); onEditingEnd?.(); }
  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f && f.type.startsWith("image/")) onPick(f); };
  const onDragOver = (e) => e.preventDefault();

  function onImgLoad(e) {
    const img = e.target, nw = img.naturalWidth, nh = img.naturalHeight;
    const scale = Math.min(VIEW_W / nw, VIEW_H / nh);
    const dw = Math.round(nw * scale), dh = Math.round(nh * scale);
    const offsetX = Math.floor((VIEW_W - dw) / 2), offsetY = Math.floor((VIEW_H - dh) / 2);
    setDisp({ w: dw, h: dh, scale, offsetX, offsetY });
    const w = Math.floor(dw * 0.8), h = Math.floor(dh * 0.6);
    setBox({ x: offsetX + Math.floor((dw - w) / 2), y: offsetY + Math.floor((dh - h) / 2), w, h });
  }

  function beginDrag(e) { e.preventDefault(); dragRef.current = { active: true, ox: "touches" in e ? e.touches[0].clientX : e.clientX, oy: "touches" in e ? e.touches[0].clientY : e.clientY, sx: box.x, sy: box.y }; }
  function beginResize(e, corner) { e.preventDefault(); resizeRef.current = { active: true, corner, ox: "touches" in e ? e.touches[0].clientX : e.clientX, oy: "touches" in e ? e.touches[0].clientY : e.clientY, x: box.x, y: box.y, w: box.w, h: box.h }; }

  useEffect(() => {
    function clamp(nx, ny, nw, nh) {
      nx = Math.max(disp.offsetX, Math.min(disp.offsetX + disp.w - nw, nx));
      ny = Math.max(disp.offsetY, Math.min(disp.offsetY + disp.h - nh, ny));
      nw = Math.max(MIN_W, Math.min(disp.w - (nx - disp.offsetX), nw));
      nh = Math.max(MIN_H, Math.min(disp.h - (ny - disp.offsetY), nh));
      return { nx, ny, nw, nh };
    }
    function onMove(e) {
      const mx = "touches" in e ? e.touches[0].clientX : e.clientX;
      const my = "touches" in e ? e.touches[0].clientY : e.clientY;
      if (resizeRef.current.active) {
        const dx = mx - resizeRef.current.ox, dy = my - resizeRef.current.oy;
        let { x, y, w, h } = resizeRef.current;
        let c;
        switch (resizeRef.current.corner) {
          case "tl": c = clamp(x + dx, y + dy, w - dx, h - dy); break;
          case "tr": c = clamp(x, y + dy, w + dx, h - dy); break;
          case "bl": c = clamp(x + dx, y, w - dx, h + dy); break;
          case "br": c = clamp(x, y, w + dx, h + dy); break;
          default: return;
        }
        setBox({ x: c.nx, y: c.ny, w: c.nw, h: c.nh });
        return;
      }
      if (!dragRef.current.active) return;
      const c = clamp(dragRef.current.sx + (mx - dragRef.current.ox), dragRef.current.sy + (my - dragRef.current.oy), box.w, box.h);
      setBox({ x: c.nx, y: c.ny, w: box.w, h: box.h });
    }
    function onUp() { dragRef.current.active = false; resizeRef.current.active = false; }
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false }); window.addEventListener("touchend", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); window.removeEventListener("touchmove", onMove); window.removeEventListener("touchend", onUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box, disp]);

  useEffect(() => {
    if (mode !== "editing" || !sourceUrl || !imgEl.current) return;
    const img = imgEl.current;
    const sx = Math.round((box.x - disp.offsetX) / disp.scale), sy = Math.round((box.y - disp.offsetY) / disp.scale);
    const sw = Math.max(1, Math.round(box.w / disp.scale)), sh = Math.max(1, Math.round(box.h / disp.scale));
    const scale = OUT_MAX / Math.max(sw, sh);
    const cnv = document.createElement("canvas"); cnv.width = Math.round(sw * scale); cnv.height = Math.round(sh * scale);
    const ctx = cnv.getContext("2d"); ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cnv.width, cnv.height);
    onEditingPreview?.(cnv.toDataURL("image/png"));
  }, [box, disp, sourceUrl, mode, onEditingPreview]);

  async function confirm() {
    const img = imgEl.current; if (!img) return;
    setBusy(true); setErr("");
    try {
      const sx = Math.round((box.x - disp.offsetX) / disp.scale), sy = Math.round((box.y - disp.offsetY) / disp.scale);
      const sw = Math.max(1, Math.round(box.w / disp.scale)), sh = Math.max(1, Math.round(box.h / disp.scale));
      const scale = OUT_MAX / Math.max(sw, sh);
      const c = document.createElement("canvas"); c.width = Math.round(sw * scale); c.height = Math.round(sh * scale);
      const ctx = c.getContext("2d"); ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, c.width, c.height);
      const blob = await new Promise((res) => c.toBlob(res, "image/png", 0.92));
      const fd = new FormData(); fd.append("file", blob, "logo.png");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand/logo`, { method: "POST", credentials: "include", body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.error || "Upload failed");
      const url = d?.file?.url || d?.brand?.logoUrl;
      if (url) onConfirmed?.(url);
      cancel();
    } catch (e) { setErr(e.message || "Upload failed"); }
    finally { setBusy(false); refresh(); }
  }

  return (
    <div>
      {mode === "idle" && (
        <DropZone onPick={onPick} isDragOver={false} onDrop={onDrop} onDragOver={onDragOver} onDragLeave={() => {}} />
      )}
      {mode === "editing" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "14px", fontWeight: 600, color: "#0c1118", marginBottom: "4px" }}>Crop Your Logo</div>
            <div style={{ fontSize: "12px", color: "#9ca3af" }}>Drag to move · corner handles to resize (free aspect)</div>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ border: "1px solid #e8ecf0", borderRadius: "8px", overflow: "hidden" }}>
              <div style={{ position: "relative", width: VIEW_W, height: VIEW_H, background: "#f4f5f7" }} onDrop={onDrop} onDragOver={onDragOver}>
                <img ref={imgEl} src={sourceUrl} alt="to-crop" onLoad={onImgLoad} draggable={false}
                  style={{ position: "absolute", left: disp.offsetX, top: disp.offsetY, width: disp.w || "auto", height: disp.h || "auto", userSelect: "none" }} />
                <div onMouseDown={beginDrag} onTouchStart={beginDrag}
                  style={{ position: "absolute", left: box.x, top: box.y, width: box.w, height: box.h, border: "2px solid #f9ab14", background: "rgba(249,171,20,.08)", cursor: "move", boxShadow: "0 0 0 9999px rgba(0,0,0,.55)", borderRadius: "3px" }} />
                {[["tl","nwse"], ["tr","nesw"], ["bl","nesw"], ["br","nwse"]].map(([c, cur]) => {
                  const pos = { tl: { left: box.x-6, top: box.y-6 }, tr: { left: box.x+box.w-6, top: box.y-6 }, bl: { left: box.x-6, top: box.y+box.h-6 }, br: { left: box.x+box.w-6, top: box.y+box.h-6 } }[c];
                  return <div key={c} style={{ position: "absolute", ...pos, width: 12, height: 12, borderRadius: "50%", background: "#f9ab14", border: "2px solid #fff", cursor: `${cur}-resize` }} onMouseDown={(e) => beginResize(e, c)} onTouchStart={(e) => beginResize(e, c)} />;
                })}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            <button className="up-btn up-btn-outline" onClick={cancel} disabled={busy}>Cancel</button>
            <button className="up-btn up-btn-dark" onClick={confirm} disabled={busy}>
              {busy ? <><div className="up-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Uploading…</> : "Confirm & Upload"}
            </button>
          </div>
          {err && <div style={{ textAlign: "center", fontSize: "12.5px", color: "#dc2626", padding: "8px", background: "rgba(239,68,68,.06)", borderRadius: "6px" }}>{err}</div>}
        </div>
      )}
    </div>
  );
}

/* ── EditorAccessCard ── */
function EditorAccessCard() {
  const { user } = useAuth();
  const isOwner = (user?.primaryOrgRole || "").toLowerCase() === "owner";
  const [saving, setSaving] = useState(false);

  // Read initial value from the primary org's settings
  const primaryOrg = user?.orgs?.find(o => o.orgId === user?.orgs?.[0]?.orgId) ?? user?.orgs?.[0];
  const [publicAccess, setPublicAccess] = useState(!!primaryOrg?.settings?.editorPublicAccess);

  async function handleToggle(next) {
    setSaving(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/org/settings/editor-access`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ publicAccess: next }),
        }
      );
      if (!res.ok) throw new Error("Failed to save");
      setPublicAccess(next);
      toast.success(next ? "Guest access enabled" : "Guest access disabled");
    } catch {
      toast.error("Could not save setting. Please try again.");
      // Revert checkbox to previous value on failure
    } finally {
      setSaving(false);
    }
  }

  if (!isOwner) return null;

  return (
    <div className="bs-access-card">
      <div style={{ fontSize: "14px", fontWeight: 600, color: "#0c1118", marginBottom: "6px" }}>3D Editor Access</div>
      <div style={{ fontSize: "12.5px", color: "#9ca3af", marginBottom: "14px" }}>Control whether visitors can open your 3D editor without logging in.</div>
      <div className="bs-toggle-wrap">
        <input
          type="checkbox"
          className="bs-toggle-input"
          checked={publicAccess}
          disabled={saving}
          onChange={(e) => handleToggle(e.target.checked)}
        />
        <span style={{ fontSize: "13px", color: "#374151" }}>
          Allow nickname / guest users to open the editor without login
        </span>
        {saving && <span style={{ fontSize: "12px", color: "#9ca3af", marginLeft: "8px" }}>Saving…</span>}
      </div>
    </div>
  );
}

/* ── LogoPreviewCard (preserved exactly) ── */
function LogoPreviewCard({ url, hasSaved, isEditing, onRemoved }) {
  async function removeLogo() {
    if (!hasSaved) return;
    if (!window.confirm("Remove your logo? This cannot be undone.")) return;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand/logo`, { method: "DELETE", credentials: "include" });
      if (r.ok) onRemoved?.();
      else { const d = await r.json().catch(() => ({})); alert(d?.error || "Failed to remove logo"); }
    } catch { alert("Failed to remove logo. Please try again."); }
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div className="bs-logo-preview-box">
        {url ? <img src={url} alt="Logo Preview" /> : (
          <div style={{ textAlign: "center" }}><Upload size={24} style={{ color: "#d1d5db", margin: "0 auto" }} /><div style={{ fontSize: "12px", color: "#d1d5db", marginTop: "6px" }}>No logo uploaded</div></div>
        )}
      </div>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "13px", fontWeight: 600, color: "#0c1118", marginBottom: "3px" }}>{isEditing ? "Live Preview" : "Current Logo"}</div>
        <div style={{ fontSize: "12px", color: "#9ca3af" }}>{isEditing ? "This is how your cropped logo will look" : "Your uploaded company logo"}</div>
      </div>
      {hasSaved && !isEditing && (
        <button className="up-btn up-btn-outline" style={{ color: "#dc2626", justifyContent: "center" }} onClick={removeLogo}><X size={13} /> Remove Logo</button>
      )}
      {isEditing && (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#6366f1", background: "rgba(99,102,241,.07)", border: "1px solid rgba(99,102,241,.2)", borderRadius: "7px", padding: "8px 12px" }}>
          <Edit3 size={13} /> Editing mode — confirm to save
        </div>
      )}
    </div>
  );
}

/* ── DropZone (preserved exactly) ── */
function DropZone({ onPick, isDragOver, onDrop, onDragOver, onDragLeave }) {
  const inputRef = useRef(null);
  return (
    <div
      style={{ border: `2px dashed ${isDragOver ? "#f9ab14" : "#e2e8f0"}`, borderRadius: "8px", minHeight: "180px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", cursor: "pointer", transition: "all 180ms", background: isDragOver ? "rgba(249,171,20,.04)" : "#fafafa", padding: "24px", gap: "12px" }}
      onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
    >
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: isDragOver ? "rgba(249,171,20,.15)" : "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <FaCloudUploadAlt style={{ fontSize: "22px", color: isDragOver ? "#f9ab14" : "#9ca3af" }} />
      </div>
      <div>
        <div style={{ fontSize: "13.5px", fontWeight: 500, color: isDragOver ? "#b45309" : "#374151", marginBottom: "3px" }}>{isDragOver ? "Drop your logo here" : "Drag & drop your logo here"}</div>
        <div style={{ fontSize: "12px", color: "#9ca3af" }}>or</div>
      </div>
      <button type="button" className="up-btn up-btn-dark" onClick={() => inputRef.current?.click()}>Choose File</button>
      <div style={{ fontSize: "11px", color: "#d1d5db" }}>PNG, JPG, or GIF up to 2 MB · Recommended: 1200×600 px or 2:1 ratio</div>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => onPick(e.target.files?.[0] || null)} />
    </div>
  );
}
